import {
  Button,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SimpleHeaderAndData,
  column,
  simpleDataRows,
  useModal,
} from "@homebound/beam";
import { useMemo } from "react";
import { useHistory } from "react-router";
import { dateCell, priceCell } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { InvoiceType, TaskStatus } from "src/generated/graphqlTypes";
import { InvoiceTermsForm } from "src/routes/billing/invoice-terms/components/InvoiceTermsForm";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  InvoiceScheduleTab_InvoiceScheduleFragment,
  InvoiceScheduleTab_InvoiceScheduleItemFragment,
  useGeneratePocInvoiceMutation,
} from "src/routes/projects/homeowner-contracts/InvoiceScheduleTab.generated";
import { createInvoiceUrl } from "src/RouteUrls";
type InvoiceScheduleItemsTableProps = { invoiceSchedule: InvoiceScheduleTab_InvoiceScheduleFragment };

export function InvoiceScheduleTab({ invoiceSchedule }: InvoiceScheduleItemsTableProps) {
  const history = useHistory();
  const { openModal, closeModal } = useModal();
  const [generatePocInvoice] = useGeneratePocInvoiceMutation();
  const rows = useMemo(() => createRows(invoiceSchedule.items), [invoiceSchedule]);
  const columns = useMemo(() => createColumns(), []);

  const { project } = invoiceSchedule;
  const showPocSection = project.enablePocInvoiceSchedule;

  const handleGenerateInvoice = async () => {
    const result = await generatePocInvoice({
      variables: { input: { projectStageId: project.latestActiveStage?.id, type: InvoiceType.PercentageOfCompletion } },
    });
    const invoiceId = result.data?.saveInvoice?.invoice?.id;
    if (invoiceId) {
      history.push(createInvoiceUrl(project.id, invoiceId));
    }
  };

  const openConfirmationModal = () => {
    openModal({
      content: (
        <ConfirmationModal
          title="Generate Progress Billing Invoice"
          label="Generate Invoice"
          onConfirmAction={handleGenerateInvoice}
          confirmationMessage={<GeneratePocInvoiceModalContent />}
        />
      ),
    });
  };

  return (
    <div>
      <div css={Css.df.ml1.mb4.$}>
        <div css={Css.md.mr1.$}> The contract billing schedule determines when the Propco is invoiced. </div>
        <div css={Css.sm.mya.$}>
          <Button
            variant="text"
            onClick={() =>
              openModal({
                content: (
                  <>
                    <ModalHeader>Invoice Terms</ModalHeader>
                    <ModalBody>
                      <InvoiceTermsForm invoiceScheduleTemplateId={invoiceSchedule?.invoiceScheduleTemplate.id} />
                    </ModalBody>
                    <ModalFooter>
                      <Button label="Close" variant="primary" onClick={closeModal} />
                    </ModalFooter>
                  </>
                ),
                drawHeaderBorder: true,
                size: "lg",
              })
            }
            label="View Terms"
          />
        </div>
      </div>

      {showPocSection && <PocInvoiceSection onGenerateClick={openConfirmationModal} />}

      <div css={Css.bgWhite.p2.br8.bshBasic.$}>
        <GridTable
          rows={rows}
          columns={columns}
          style={{ allWhite: true }}
          fallbackMessage="No Invoice Schedule Items found for the project"
        />
      </div>
    </div>
  );
}

type Row = SimpleHeaderAndData<InvoiceScheduleTab_InvoiceScheduleItemFragment>;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Order", data: (row) => row.order + 1, w: "60px" }),
    column<Row>({ header: "Name", data: (row) => row.parent?.name ?? row.globalParent?.name, w: 2 }),
    column<Row>({
      header: "Status",
      data: (row) => {
        const status =
          row.parent?.__typename === "PlanMilestone"
            ? row.parent.taskStatusRollup
            : row.parent?.__typename === "ScheduleTask"
              ? row.parent.statusDetail
              : { code: TaskStatus.NotStarted, name: "Not Started" };
        return (
          status && (
            <div css={Css.df.aic.$}>
              <StatusIndicator status={status.code} />
              <div css={Css.ml1.$}>{status.name}</div>
            </div>
          )
        );
      },
    }),
    column<Row>({ header: "Invoice Date", data: (row) => dateCell(row.invoiceDate) }),
    column<Row>({ header: "Original Amount", data: (row) => priceCell({ valueInCents: row.originalAmountInCents }) }),
    column<Row>({
      header: "Change Order Amount",
      data: (row) => priceCell({ valueInCents: row.changeOrderAmountInCents }),
    }),
    column<Row>({ header: "Revised Amount", data: (row) => priceCell({ valueInCents: row.revisedAmountInCents }) }),
    column<Row>({
      header: "Invoice Status",
      data: (row) =>
        row.invoiceScheduledLineItems.first ? (
          <div css={Css.df.aic.$}>
            <StatusIndicator status={row.invoiceScheduledLineItems.first.invoice.status.code} />
            <div css={Css.ml1.$}>{row.invoiceScheduledLineItems.first.invoice.status.name}</div>
          </div>
        ) : undefined,
    }),
    column<Row>({ header: "Invoice Identifier", data: (row) => row.invoiceScheduledLineItems.first?.invoice.title }),
  ];
}

function createRows(data: InvoiceScheduleTab_InvoiceScheduleItemFragment[]): GridDataRow<Row>[] {
  return simpleDataRows(data);
}

type PocInvoiceSectionProps = { onGenerateClick: () => void };

function PocInvoiceSection({ onGenerateClick }: PocInvoiceSectionProps) {
  return (
    <div css={Css.bgWhite.p2.br8.bshBasic.mb4.$}>
      <div css={Css.df.fdc.gap2.$}>
        <div css={Css.smSb.$}>Generate Percentage of Completion Invoice</div>
        <div css={Css.sm.$}>
          Progress Billing invoices are calculated using the <b>Percentage of Completion (POC)</b> method, billing only
          for work completed or materials delivered. The invoice amount equals the{" "}
          <b>contract price multiplied by the POC</b>, which is calculated from{" "}
          <b>cost accruals and expenses divided by the revised internal budget. Deposit invoices are excluded.</b>
          <br />
          The <b>invoice category</b> on each cost code determines whether <b>Construction or Pre-Construction</b>{" "}
          pricing and budget values are used. When generated <b>manually</b>, the invoice will include all{" "}
          <b>uninvoiced cost accruals and expenses</b>.
        </div>
        <div>
          <Button label="Generate Progress Billing Invoice" onClick={onGenerateClick} />
        </div>
      </div>
    </div>
  );
}

function GeneratePocInvoiceModalContent() {
  return (
    <div css={Css.df.fdc.gap2.$}>
      <p>
        You are about to generate a Progress Billing invoice based on current cost accruals and percentage of
        completion.
      </p>
      <div>
        <p css={Css.smSb.mb1.$}>Before proceeding, please verify:</p>
        <ul css={Css.ml2.$}>
          <li>All completed work has been properly recorded</li>
          <li>Cost accruals are up to date</li>
          <li>This is the intended billing period</li>
        </ul>
      </div>
      <p>
        If an invoice is generated in error, it must go through the rejection workflow and any associated costs will
        carry over to the next invoice period.
      </p>
      <p css={Css.smSb.$}>Are you sure you want to generate this invoice?</p>
    </div>
  );
}
